import { useCallback, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, Rise } from "./Reveal";
import EventCard from "./EventCard";
import MonthCalendar from "./MonthCalendar";
import WeekEvents from "./WeekEvents";
import CountdownBanner from "./CountdownBanner";
import content from "../content";
import { SectionBg } from "./SectionBg";

export default function EsteMesCarousel() {
  const { esteMes } = content;
  const trackRef = useRef(null);
  const rafRef = useRef(0);

  const applyCoverflow = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const center = track.scrollLeft + track.clientWidth / 2;
    Array.from(track.children).forEach((child) => {
      const childCenter = child.offsetLeft + child.offsetWidth / 2;
      const dist = (childCenter - center) / track.clientWidth;
      const c = Math.max(-1.3, Math.min(1.3, dist));
      const abs = Math.min(Math.abs(c), 1);
      const rotateY = c * -30;
      const scale = 1 - abs * 0.2;
      const opacity = 1 - abs * 0.45;
      const translateZ = -abs * 130;
      child.style.transform = `perspective(1400px) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`;
      child.style.opacity = `${opacity}`;
      child.style.zIndex = `${100 - Math.round(abs * 100)}`;
    });
  }, []);

  const onScroll = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(applyCoverflow);
  }, [applyCoverflow]);

  useEffect(() => {
    applyCoverflow();
    window.addEventListener("resize", applyCoverflow);
    return () => window.removeEventListener("resize", applyCoverflow);
  }, [applyCoverflow]);

  const nudge = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[0];
    const step = card ? card.offsetWidth + 24 : track.clientWidth * 0.8;
    track.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section id="este-mes" className="relative w-full bg-ink py-28 md:py-36 overflow-hidden" data-testid="este-mes-section">
      <SectionBg image="/images/este-mes-bg.webp" tint="ink" />
      <div className="relative w-full max-w-7xl mx-auto px-6">
        <Reveal className="flex items-end justify-between gap-6 mb-4">
          <div>
            <Rise>
              <p className="eyebrow mb-4">{esteMes.eyebrow}</p>
            </Rise>
            <Rise>
              <h2 className="display text-white text-[clamp(2.5rem,7vw,5.5rem)]">
                Lo que viene<span className="text-electric">.</span>
              </h2>
            </Rise>
          </div>
          {esteMes.events.length > 0 && (
          <div className="hidden md:flex gap-3">
            <button
              onClick={() => nudge(-1)}
              className="w-12 h-12 rounded-full border border-white/15 bg-white/5 backdrop-blur-md flex items-center justify-center text-white hover:bg-electric hover:border-electric hover:shadow-[0_0_24px_rgba(33,112,232,0.6)] transition-colors duration-300 cursor-pointer"
              aria-label="Anterior"
              data-testid="carousel-prev"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              onClick={() => nudge(1)}
              className="w-12 h-12 rounded-full border border-white/15 bg-white/5 backdrop-blur-md flex items-center justify-center text-white hover:bg-electric hover:border-electric hover:shadow-[0_0_24px_rgba(33,112,232,0.6)] transition-colors duration-300 cursor-pointer"
              aria-label="Siguiente"
              data-testid="carousel-next"
            >
              <ChevronRight size={22} />
            </button>
          </div>
          )}
        </Reveal>
      </div>

      {esteMes.events.length > 0 && (
        <>
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="relative no-scrollbar flex gap-6 overflow-x-auto snap-x snap-mandatory py-10 px-[calc(50vw-42vw)] sm:px-[calc(50%-220px)]"
        style={{ perspective: "1400px" }}
        data-testid="este-mes-carousel"
      >
        {esteMes.events.map((ev, i) => (
          <div
            key={ev.image}
            className="coverflow-card snap-center shrink-0 w-[84vw] sm:w-[400px]"
          >
            <EventCard event={ev} testId={`event-card-${i}`} />
          </div>
        ))}
      </div>

      <p className="relative text-center font-poppins text-xs uppercase tracking-[0.25em] text-white/40 mt-2 md:hidden">
        Desliza para ver más →
      </p>
        </>
      )}

      <CountdownBanner />
      <WeekEvents />

      {esteMes.calendar && (
        <div className="relative w-full max-w-7xl mx-auto px-6">
          <MonthCalendar />
        </div>
      )}
    </section>
  );
}
